import type { RetailerBottomNavLink } from "@faire/prototype-playground/components/RetailerGlobalNavLoggedIn";
import RetailerLayout from "@faire/prototype-playground/components/RetailerLayout";
import { CompassProvider } from "@faire/prototype-playground/contexts/CompassContext";
import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import type { MouseEvent } from "react";
import { useEffect, useRef, useState } from "react";

import {
  BENEFITS_MENU_ID,
  BenefitsMegaMenu,
  type BenefitsMenuLayoutVariant,
} from "./BenefitsMegaMenu";
import { BenefitStrip } from "./BenefitStrip";
import { MarketplaceHomepage } from "./MarketplaceHomepage";

const PRODUCTION_CATEGORY_LINKS: RetailerBottomNavLink[] = [
  { label: "Bestsellers", href: "#bestsellers" },
  { label: "New products", href: "#new-products" },
  { label: "New brands", href: "#new-brands" },
  { label: "Trending", href: "#trending" },
  { label: "Local", href: "#local" },
  { label: "Insider", href: "#insider" },
  { label: "Food & drink", href: "#food-and-drink" },
  { label: "Women", href: "#women" },
  { label: "Fall Shop", href: "#fall-shop" },
  { label: "Faire Source", href: "#faire-source" },
  { label: "Sale", href: "#sale", isSale: true },
];

const EXPERIENCE_VARIANT = {
  benefitStrip: 1,
  compactLedger: 2,
  editorialSplit: 3,
  iconRail: 4,
  denseCards: 5,
  featureSpotlight: 6,
} as const;

type ExperienceVariant =
  (typeof EXPERIENCE_VARIANT)[keyof typeof EXPERIENCE_VARIANT];

const MENU_CLOSE_DELAY_MS = 160;
const BENEFITS_TRIGGER_ID = "benefits-nav-trigger";

export const prototypeMeta: PrototypeMetadata = {
  name: "One banner to rule them all",
  owner: "Vikas Lalwani",
  email: "vikas.lalwani@faire.com",
  status: "in-progress",
  platform: "web",
  createdAt: "2026-08-05",
  lastUpdated: "2026-08-05",
  surfaceArea: "Retailer",
  category: "experimental",
  creationTool: "claude-code",
  thumbnail: "/images/thumbnails/vikas-one-banner-to-rule-them-all.png",
  description:
    "A persistent, benefit-led home for Faire programs and retailer growth tools.",
};

export default function OneBannerToRuleThemAllPage() {
  const [experienceVariant, setExperienceVariant] = useState<ExperienceVariant>(
    EXPERIENCE_VARIANT.benefitStrip
  );
  const [isBenefitsMenuOpen, setIsBenefitsMenuOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const suppressNextFocusOpenRef = useRef(false);
  const growthLabel = usePlaygroundLocalMsg("Growth");
  const growthStripLabel = usePlaygroundLocalMsg("Growth strip");
  const growthSurfaceLabel = usePlaygroundLocalMsg("Growth surface");

  useEffect(() => {
    document.title = prototypeMeta.name;

    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const cancelMenuClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openBenefitsMenu = () => {
    if (suppressNextFocusOpenRef.current) {
      suppressNextFocusOpenRef.current = false;
      return;
    }
    cancelMenuClose();
    setIsBenefitsMenuOpen(true);
  };

  const scheduleMenuClose = () => {
    cancelMenuClose();
    closeTimerRef.current = window.setTimeout(() => {
      setIsBenefitsMenuOpen(false);
      closeTimerRef.current = null;
    }, MENU_CLOSE_DELAY_MS);
  };

  const handleVariantChange = (variantNumber: number) => {
    setExperienceVariant(variantNumber as ExperienceVariant);
    setIsBenefitsMenuOpen(false);
  };

  const handleBenefitsClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    openBenefitsMenu();
  };

  const closeBenefitsMenu = () => {
    cancelMenuClose();
    setIsBenefitsMenuOpen(false);
  };

  const closeBenefitsMenuAndRestoreFocus = () => {
    suppressNextFocusOpenRef.current = true;
    closeBenefitsMenu();
    window.requestAnimationFrame(() => {
      document.getElementById(BENEFITS_TRIGGER_ID)?.focus();
    });
  };

  const showsBenefitsMenu =
    experienceVariant !== EXPERIENCE_VARIANT.benefitStrip;
  const benefitsMenuLayoutVariant = Math.max(
    1,
    experienceVariant - 1
  ) as BenefitsMenuLayoutVariant;

  const bottomNavLinks: RetailerBottomNavLink[] = showsBenefitsMenu
    ? [
        ...PRODUCTION_CATEGORY_LINKS,
        {
          label: growthLabel,
          href: "#growth",
          id: BENEFITS_TRIGGER_ID,
          ariaControls: BENEFITS_MENU_ID,
          ariaExpanded: isBenefitsMenuOpen,
          ariaHasPopup: "dialog",
          onBlur: scheduleMenuClose,
          onClick: handleBenefitsClick,
          onFocus: openBenefitsMenu,
          onMouseEnter: openBenefitsMenu,
          onMouseLeave: scheduleMenuClose,
          popover: (
            <BenefitsMegaMenu
              key={isBenefitsMenuOpen ? "benefits-open" : "benefits-closed"}
              isOpen={isBenefitsMenuOpen}
              layoutVariant={benefitsMenuLayoutVariant}
              onBlur={scheduleMenuClose}
              onFocus={openBenefitsMenu}
              onMouseEnter={openBenefitsMenu}
              onMouseLeave={scheduleMenuClose}
              onNavigate={closeBenefitsMenu}
              onRequestClose={closeBenefitsMenuAndRestoreFocus}
            />
          ),
        },
      ]
    : PRODUCTION_CATEGORY_LINKS;

  return (
    <CompassProvider>
      <LiveToolbar
        hiddenOnMobile
        variantGroups={[
          {
            title: growthSurfaceLabel,
            queryKey: "benefits",
            variants: [
              {
                variantNumber: EXPERIENCE_VARIANT.benefitStrip,
                label: growthStripLabel,
              },
              {
                variantNumber: EXPERIENCE_VARIANT.compactLedger,
                label: "Compact ledger",
              },
              {
                variantNumber: EXPERIENCE_VARIANT.editorialSplit,
                label: "Editorial split",
              },
              {
                variantNumber: EXPERIENCE_VARIANT.iconRail,
                label: "Icon rail",
              },
              {
                variantNumber: EXPERIENCE_VARIANT.denseCards,
                label: "Dense cards",
              },
              {
                variantNumber: EXPERIENCE_VARIANT.featureSpotlight,
                label: "Feature spotlight",
              },
            ],
            active: experienceVariant,
            onChange: handleVariantChange,
          },
        ]}
      />
      <RetailerLayout
        bottomNavLinksOverride={bottomNavLinks}
        cartCount={4}
        hideFooter
        languageSelector
      >
        {experienceVariant === EXPERIENCE_VARIANT.benefitStrip ? (
          <BenefitStrip />
        ) : null}
        <MarketplaceHomepage />
      </RetailerLayout>
    </CompassProvider>
  );
}
